// Mercapoli - Liquidaciones del Comercio
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../../components/merchant/MerchantHeader';
import { 
  ChevronLeft, Download, Wallet, Calendar, CheckCircle, 
  Clock, FileText, DollarSign, AlertCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { comercio as comercioApi } from '../../lib/api';
import { toast } from 'sonner';


const MerchantLiquidaciones = () => {
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [resumen, setResumen] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [liquidacionesData, resumenData] = await Promise.all([
          comercioApi.getLiquidaciones(),
          comercioApi.getResumenVentas('mes')
        ]);
        setLiquidaciones(liquidacionesData);
        setResumen(resumenData);
      } catch (error) {
        console.error('Error:', error);
        toast.error('Error al cargar liquidaciones');
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const formatMoney = (amount) => {
    const simbolo = resumen?.simbolo || 'Q';
    return `${simbolo}${(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    return new Date(fecha).toLocaleDateString('es-GT', { 
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const handleDownloadPdf = (liquidacionId) => {
    const url = comercioApi.descargarLiquidacionPdf(liquidacionId);
    window.open(url, '_blank');
  };

  const totalPagado = liquidaciones
    .filter(l => l.estado === 'pagado')
    .reduce((sum, l) => sum + (l.monto_neto || l.neto_comercio || 0), 0);

  const totalPendiente = liquidaciones
    .filter(l => l.estado === 'pendiente')
    .reduce((sum, l) => sum + (l.monto_neto || l.neto_comercio || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header con Logout */}
      <MerchantHeader title="Liquidaciones" />

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Info Card */}
        <Card className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-950/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-blue-800 dark:text-blue-200">
                  ¿Cómo funcionan las liquidaciones?
                </p>
                <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                  Las liquidaciones se generan semanalmente. Incluyen el total de tus ventas, 
                  la comisión de la plataforma y el monto neto a recibir. Una vez procesado el pago, 
                  recibirás un email con el comprobante.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <Wallet className="h-6 w-6 mx-auto mb-2 text-brand-primary" />
              <p className="text-2xl font-bold">{liquidaciones.length}</p>
              <p className="text-xs text-muted-foreground">Total Liquidaciones</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 dark:bg-green-950/20">
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-6 w-6 mx-auto mb-2 text-green-600" />
              <p className="text-2xl font-bold text-green-600">{formatMoney(totalPagado)}</p>
              <p className="text-xs text-muted-foreground">Total Pagado</p>
            </CardContent>
          </Card>
          <Card className="bg-yellow-50 dark:bg-yellow-950/20 col-span-2 lg:col-span-1">
            <CardContent className="p-4 text-center">
              <Clock className="h-6 w-6 mx-auto mb-2 text-yellow-600" />
              <p className="text-2xl font-bold text-yellow-600">{formatMoney(totalPendiente)}</p>
              <p className="text-xs text-muted-foreground">Pendiente de Pago</p>
            </CardContent>
          </Card>
        </div>

        {/* Liquidaciones List */}
        {liquidaciones.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No hay liquidaciones aún</p>
              <p className="text-sm text-muted-foreground mt-2">
                Las liquidaciones se generan automáticamente cada semana
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {liquidaciones.map((liquidacion) => (
              <Card 
                key={liquidacion.liquidacion_id}
                className={liquidacion.estado === 'pagado' ? 'border-green-200' : 'border-yellow-200'}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold">Liquidación #{liquidacion.liquidacion_id?.slice(-8)}</h3>
                        {liquidacion.estado === 'pagado' ? (
                          <Badge className="bg-green-500">
                            <CheckCircle className="h-3 w-3 mr-1" /> Pagado
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="border-yellow-500 text-yellow-600">
                            <Clock className="h-3 w-3 mr-1" /> Pendiente
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Calendar className="h-4 w-4" />
                        <span>
                          {formatFecha(liquidacion.periodo_inicio)} - {formatFecha(liquidacion.periodo_fin)}
                        </span>
                      </div>
                    </div>

                    {liquidacion.pdf_url && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadPdf(liquidacion.liquidacion_id)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        PDF
                      </Button>
                    )}
                  </div>

                  {/* Desglose */}
                  <div className="grid grid-cols-2 gap-4 p-3 bg-muted rounded-lg">
                    <div>
                      <p className="text-xs text-muted-foreground">Ventas Brutas</p>
                      <p className="font-medium">{formatMoney(liquidacion.ventas_brutas || liquidacion.total_ventas)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pedidos</p>
                      <p className="font-medium">{liquidacion.total_pedidos}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Comisión ({liquidacion.comision_porcentaje}%)</p>
                      <p className="font-medium text-red-500">-{formatMoney(liquidacion.comision_monto || liquidacion.comision_total)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Neto a Recibir</p>
                      <p className="font-bold text-green-600">{formatMoney(liquidacion.monto_neto || liquidacion.neto_comercio)}</p>
                    </div>
                  </div>

                  {/* Fecha de pago */}
                  {liquidacion.estado === 'pagado' && liquidacion.fecha_pago && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="h-4 w-4" />
                      <span>Pagado el {formatFecha(liquidacion.fecha_pago)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default MerchantLiquidaciones;
